import React from "react";
import {
  FaTools,
  FaDesktop,
  FaServer,
  FaHeadset,
  FaClock,
  FaCheckDouble,
} from "react-icons/fa";
import "../styles/ServicePage.css";

// Constants
const TECH_REPAIR_SERVICES = [
  {
    id: 1,
    title: "Hardware Repair",
    icon: FaDesktop,
    description:
      "Professional repair and maintenance of computers, laptops, and peripherals.",
    features: [
      "Laptop Repair",
      "Desktop Repair",
      "Printer & Scanner Repair",
      "Network Equipment Service",
      "Data Recovery",
      "Component Replacement",
    ],
    benefits: [
      "Extended equipment life",
      "Reduced downtime",
      "Data recovery",
      "Cost savings vs replacement",
      "Expert diagnostics",
      "Warranty on repairs",
    ],
  },
  {
    id: 2,
    title: "Software Support",
    icon: FaTools,
    description:
      "Comprehensive software troubleshooting, installation, and optimization.",
    features: [
      "Software Installation & Setup",
      "Virus & Malware Removal",
      "System Optimization",
      "Software Troubleshooting",
      "Operating System Upgrades",
      "License Management",
    ],
    benefits: [
      "Improved performance",
      "Secure systems",
      "Reduced crashes",
      "Faster startup times",
      "Smooth operations",
      "License compliance",
    ],
  },
  {
    id: 3,
    title: "IT Infrastructure Setup",
    icon: FaServer,
    description:
      "Complete IT infrastructure design, implementation, and management.",
    features: [
      "Network Setup & Configuration",
      "Server Installation",
      "Wireless Network Setup",
      "Cloud Integration",
      "Backup Solutions",
      "System Monitoring",
    ],
    benefits: [
      "Reliable connectivity",
      "Improved productivity",
      "Scalable systems",
      "Data protection",
      "Reduced IT costs",
      "Minimal downtime",
    ],
  },
  {
    id: 4,
    title: "24/7 Technical Support",
    icon: FaHeadset,
    description:
      "Round-the-clock support for all your technical needs and emergencies.",
    features: [
      "Phone & Email Support",
      "Remote Assistance",
      "On-Site Support",
      "Emergency Response",
      "Ticket Tracking",
      "Knowledge Base Access",
    ],
    benefits: [
      "Rapid response times",
      "Minimize downtime",
      "Expert assistance",
      "Peace of mind",
      "Flexible support plans",
      "Priority resolution",
    ],
  },
];

const WHY_CHOOSE_US = [
  {
    icon: FaClock,
    title: "24/7 Availability",
    description: "Always here when you need us",
  },
  {
    icon: FaCheckDouble,
    title: "Certified Technicians",
    description: "Expert and experienced professionals",
  },
  {
    icon: FaHeadset,
    title: "Fast Response",
    description: "Quick solutions to minimize downtime",
  },
];

// Subcomponents
const ServiceCard = ({ service }) => {
  const IconComponent = service.icon;

  return (
    <div className="service-detail-card">
      <div className="service-detail-header">
        <IconComponent className="service-detail-icon" aria-hidden="true" />
        <div>
          <h3>{service.title}</h3>
          <p className="service-detail-desc">{service.description}</p>
        </div>
      </div>

      <div className="service-detail-content">
        <div className="detail-section">
          <h4>What We Offer</h4>
          <ul className="features-list">
            {service.features.map((feature, idx) => (
              <li key={idx}>✓ {feature}</li>
            ))}
          </ul>
        </div>

        <div className="detail-section">
          <h4>Key Benefits</h4>
          <ul className="benefits-list">
            {service.benefits.map((benefit, idx) => (
              <li key={idx}>★ {benefit}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const WhyChooseSection = () => (
  <div className="service-intro">
    <h2>Why Choose Our Tech Repair & Support?</h2>
    <p>
      From hardware repairs to software support, our certified technicians
      provide reliable, fast solutions to keep your business running without
      interruption. Available 24/7 for your peace of mind.
    </p>
    <div className="why-choose-grid">
      {WHY_CHOOSE_US.map((item, idx) => {
        const IconComponent = item.icon;
        return (
          <div key={idx} className="why-item">
            <IconComponent className="why-icon" aria-hidden="true" />
            <h3>{item.title}</h3>
            <p>{item.description}</p>
          </div>
        );
      })}
    </div>
  </div>
);

const CTASection = ({ onServiceClick }) => (
  <div className="cta-section">
    <h2>Ready to Get Started?</h2>
    <p>Contact today for reliable tech support and repair solutions</p>
    <button
      className="cta-button"
      onClick={() => onServiceClick && onServiceClick("Tech Repair & Support")}
    >
      Contact Now
    </button>
  </div>
);

// Main Component
function TechRepair({ onServiceClick }) {
  return (
    <div className="service-page">
      <div className="service-hero">
        <h1>Tech Repair & Support Solutions</h1>
        <p>
          Professional technical support keeping your systems running smoothly
          24/7
        </p>
      </div>

      <div className="service-container">
        <WhyChooseSection />

        <section
          className="services-section"
          aria-labelledby="services-heading"
        >
          <h2 id="services-heading" className="services-heading">
            Our Services
          </h2>

          {TECH_REPAIR_SERVICES.map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </section>

        <CTASection onServiceClick={onServiceClick} />
      </div>
    </div>
  );
}

export default TechRepair;
